import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventPost, EventMetadata, Perspective } from '@feature/event-detail/types';
import { EventHeader } from './EventHeader';
import { EventChat } from './EventChat';
import { SplitScreen } from './SplitScreen';
import { PerspectivesCarousel } from './PerspectivesCarousel';
import { useEventPerspectives } from '@feature/event-detail/hooks/useEventPerspectives';
import { useEventPerspectivesRealtime } from '@feature/event-detail/hooks/useEventPerspectivesRealtime';
import { VideoPlayerModal } from '@feature/play-video';
import { LivestreamViewerModal } from './LivestreamViewerModal';

interface Props {
  event: EventPost;
  metadata: EventMetadata;
}

export function EventDetailDuring({ event, metadata }: Props): React.ReactElement {
  const navigate = useNavigate();

  const { perspectives: apiPerspectives } = useEventPerspectives({
    eventId: event.id.toString(),
  });

  const { perspectives } = useEventPerspectivesRealtime({
    eventId: event.id.toString(),
    initialPerspectives: apiPerspectives,
    onEventDeleted: () => {
      alert('This event has been deleted by the creator.');
      navigate('/');
    },
  });

  const [videoModalState, setVideoModalState] = useState<{
    isOpen: boolean;
    videoUrl: string;
  }>({
    isOpen: false,
    videoUrl: '',
  });

  const [livestreamModalState, setLivestreamModalState] = useState<{
    isOpen: boolean;
    livestreamId: string;
  }>({
    isOpen: false,
    livestreamId: '',
  });

  const handleInfoPress = useCallback(() => {
    navigate(`/event/${event.share_id}/details`);
  }, [navigate, event.share_id]);

  const handlePerspectiveTap = useCallback((perspective: Perspective) => {
    const hasVod = Boolean(perspective.m3u8Url);
    const isLive = perspective.streamStatus === 'live' && perspective.livestreamId;

    if (hasVod) {
      setVideoModalState({
        isOpen: true,
        videoUrl: perspective.m3u8Url!,
      });
    } else if (isLive) {
      setLivestreamModalState({
        isOpen: true,
        livestreamId: perspective.livestreamId!,
      });
    }
  }, []);

  const topContent = useMemo(() => (
    <div style={styles.topContentWrapper}>
      <PerspectivesCarousel
        perspectives={perspectives}
        onPerspectiveTap={handlePerspectiveTap}
      />
    </div>
  ), [perspectives, handlePerspectiveTap]);

  const bottomContent = useMemo(() => (
    <EventChat chatChannelId={event.chat_channel_id ?? undefined} eventId={event.id.toString()} />
  ), [event.id, event.chat_channel_id]);

  return (
    <div style={styles.container}>
      <EventHeader
        event={event}
        metadata={metadata}
        mode="during"
        status="live"
        onInfoPress={handleInfoPress}
      />

      <div style={styles.contentWrapper}>
        <SplitScreen
          topContent={topContent}
          bottomContent={bottomContent}
        />
      </div>

      {videoModalState.isOpen && videoModalState.videoUrl && (
        <VideoPlayerModal
          isOpen={videoModalState.isOpen}
          videoUrl={videoModalState.videoUrl}
          onClose={() => {
            setVideoModalState({ isOpen: false, videoUrl: '' });
          }}
          playing
          controls
        />
      )}

      {livestreamModalState.isOpen && livestreamModalState.livestreamId && (
        <LivestreamViewerModal
          isOpen={livestreamModalState.isOpen}
          livestreamId={livestreamModalState.livestreamId}
          onClose={() => {
            setLivestreamModalState({ isOpen: false, livestreamId: '' });
          }}
        />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(var(--vh, 1vh) * 100)',
    backgroundColor: '#fff',
  },
  contentWrapper: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  topContentWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    paddingTop: 0,
  },
};
